import React, { useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';

const DeleteAccountSettings = ({ onDelete }) => {
  const [confirmText, setConfirmText] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = () => {
    setShowConfirm(true);
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setConfirmText('');
  };

  const handleConfirmDelete = async () => {
    if (confirmText !== 'DELETE') {
      return;
    }

    // Prompt for password
    const password = window.prompt('Please enter your password to confirm account deletion:');
    if (!password) {
      return;
    }

    setIsDeleting(true);
    try {
      await onDelete(password);
    } catch (error) {
      console.error('Failed to delete account:', error);
      setIsDeleting(false);
    }
  };

  const isConfirmValid = confirmText === 'DELETE';

  return (
    <div className="bg-white rounded-lg border border-red-300 sm:border-red-400 p-3 sm:p-4 md:p-6">
      <div className="flex items-center space-x-2 mb-3 sm:mb-4">
        <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
        <h2 className="text-lg sm:text-xl font-bold text-red-600">Delete Account</h2>
      </div>

      {!showConfirm ? (
        <div className="space-y-3 sm:space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-red-800 mb-2">
              <strong>Warning:</strong> This action cannot be undone. Deleting your account will:
            </p>
            <ul className="list-disc list-inside space-y-1 text-xs sm:text-sm text-red-700 ml-2">
              <li>Permanently delete all your data</li>
              <li>Cancel all active bookings</li>
              <li>Remove all your reviews and ratings</li>
              <li>Delete your payment methods</li>
              <li>Remove access to your account history</li>
            </ul>
          </div>
          <button
            onClick={handleDeleteClick}
            className="flex items-center justify-center space-x-2 bg-red-600 text-white px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg hover:bg-red-700 transition-colors w-full sm:w-auto text-sm sm:text-base active:scale-95"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete My Account</span>
          </button>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          <div className="bg-red-50 border-2 border-red-400 rounded-lg p-3 sm:p-4">
            <p className="text-xs sm:text-sm font-semibold text-red-800 mb-2">
              Are you absolutely sure you want to delete your account?
            </p>
            <p className="text-xs sm:text-sm text-red-700 mb-3">
              This will permanently delete your account and all associated data. This action cannot be reversed.
            </p>
            <div className="mt-3 sm:mt-4">
              <label className="block text-xs sm:text-sm font-medium text-red-800 mb-1.5 sm:mb-2">
                Type <span className="font-mono font-bold">DELETE</span> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                className="w-full px-3 py-2 sm:px-4 sm:py-2.5 text-sm sm:text-base border-2 border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={handleCancel}
              disabled={isDeleting}
              className="flex-1 sm:flex-initial px-4 py-2.5 sm:px-6 sm:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base disabled:opacity-50 active:scale-95"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={!isConfirmValid || isDeleting}
              className="flex-1 sm:flex-initial px-4 py-2.5 sm:px-6 sm:py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
            >
              {isDeleting ? (
                <span className="flex items-center justify-center space-x-2">
                  <span className="animate-spin">⏳</span>
                  <span>Deleting...</span>
                </span>
              ) : (
                <span className="flex items-center justify-center space-x-2">
                  <Trash2 className="w-4 h-4" />
                  <span>Permanently Delete Account</span>
                </span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteAccountSettings;

